import type { PublicPriceListItem } from '~/repositories/publicSiteRepository'

/**
 * The order cart for a tenant's public site.
 *
 * Holds only offering ids, quantities and chosen surcharge keys — never
 * prices. Every figure the customer sees comes from POST
 * /api/public/orders/quote, so the running total is the server's arithmetic
 * and matches what they will actually be charged. A cart that did its own
 * sums would drift the moment the agency repriced something, and a cart
 * that POSTED its own sums would let anyone pay 1₮ for a diploma.
 *
 * Persisted per tenant in localStorage: a visitor comparing two agencies
 * must not see one's cart on the other's site. Reads and writes are wrapped
 * because storage throws in private mode and in some embedded webviews —
 * a broken cart must not take the whole page down with it.
 */
export interface CartLine {
  offering_id: number
  quantity: number
  /** Cached for display only; the server re-derives everything that matters. */
  name: string
  unit: string
}

const KEY = (slug: string) => `order-cart:${slug}`

export function useCart() {
  const tenant = useTenant()
  const slug = computed(() => tenant.tenant.value?.slug ?? 'default')

  const lines = useState<CartLine[]>('order-cart-lines', () => [])
  const modifiers = useState<Record<string, string>>('order-cart-modifiers', () => ({}))
  const hydrated = useState<boolean>('order-cart-hydrated', () => false)

  function read<T>(suffix: string, fallback: T): T {
    if (!import.meta.client) return fallback
    try {
      const raw = localStorage.getItem(KEY(slug.value) + suffix)
      return raw ? JSON.parse(raw) as T : fallback
    } catch {
      return fallback
    }
  }

  function write(suffix: string, value: unknown) {
    if (!import.meta.client) return
    try {
      localStorage.setItem(KEY(slug.value) + suffix, JSON.stringify(value))
    } catch {
      // Quota or private mode — the cart still works for this page view.
    }
  }

  /** Call once on the client; SSR must not touch localStorage. */
  function hydrate() {
    if (!import.meta.client || hydrated.value) return
    lines.value = read<CartLine[]>('', [])
    modifiers.value = read<Record<string, string>>(':modifiers', {})
    hydrated.value = true
  }

  function persist() {
    write('', lines.value)
    write(':modifiers', modifiers.value)
  }

  function add(item: PublicPriceListItem, quantity = 1) {
    const existing = lines.value.find(l => l.offering_id === item.id)
    if (existing) {
      existing.quantity += quantity
    } else {
      lines.value.push({ offering_id: item.id, quantity, name: item.name, unit: item.unit })
    }
    persist()
  }

  function setQuantity(offeringId: number, quantity: number) {
    const line = lines.value.find(l => l.offering_id === offeringId)
    if (!line) return
    // Quantity is a count of documents or pages; below one it is a removal.
    if (quantity < 1) return remove(offeringId)
    line.quantity = Math.min(Math.floor(quantity), 1000)
    persist()
  }

  function remove(offeringId: number) {
    lines.value = lines.value.filter(l => l.offering_id !== offeringId)
    persist()
  }

  function clear() {
    lines.value = []
    modifiers.value = {}
    persist()
  }

  /** One selection per group ("urgency" → "urgent"); setting '' clears it. */
  function setModifier(group: string, key: string) {
    // Rebuild rather than `delete`: the object is replaced anyway to trigger
    // reactivity, and a dynamic delete is both slower and lint-rejected.
    const next = { ...modifiers.value }
    if (key) next[group] = key
    else Reflect.deleteProperty(next, group)
    modifiers.value = next
    persist()
  }

  const count = computed(() => lines.value.reduce((n, l) => n + l.quantity, 0))
  const isEmpty = computed(() => lines.value.length === 0)
  const has = (offeringId: number) => lines.value.some(l => l.offering_id === offeringId)

  /** The shape both /orders/quote and /orders expect. */
  const payload = computed(() => ({
    lines: lines.value.map(l => ({ offering_id: l.offering_id, quantity: l.quantity })),
    modifiers: Object.entries(modifiers.value).map(([g, k]) => `${g}:${k}`)
  }))

  return { lines, modifiers, count, isEmpty, has, hydrate, add, setQuantity, remove, clear, setModifier, payload }
}
